import { enforce, omitWhen, test } from 'vest';

import {
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface FiasService {
  dialect: 'oracle_ifc8' | 'protel';
  host: string;
  port: number | null;
  charset: string;
  linkAliveTimeoutS: number | null;
  timezone: string;
  defaultArrivalTime: string;
  defaultDepartureTime: string;
  displayGuestMessages: boolean;
}

export const FIAS_DIALECTS = ['oracle_ifc8', 'protel'];

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const SERVICE = 'currentConfig.service';

const isPort = (value: number | null) =>
  Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 65535;

const defaultConfig = (): IntegrationConfig => ({
  service: {
    dialect: 'oracle_ifc8',
    host: '',
    port: null,
    charset: 'ISO-8859-1',
    linkAliveTimeoutS: null,
    timezone: '',
    defaultArrivalTime: '',
    defaultDepartureTime: '',
    displayGuestMessages: false,
  },
  features: {},
  cron: [],
  webhooks: [],
});

export const fias: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as FiasService;

    test(`${SERVICE}.dialect`, t('validations.invalid'), () => {
      enforce(service.dialect).inside(FIAS_DIALECTS);
    });

    test(`${SERVICE}.host`, t('validations.required'), () => {
      enforce(service.host).isNotBlank();
    });

    test(`${SERVICE}.port`, t('validations.required'), () => {
      enforce(isPort(service.port)).isTruthy();
    });

    test(`${SERVICE}.charset`, t('validations.required'), () => {
      enforce(service.charset).isNotBlank();
    });

    omitWhen(service.linkAliveTimeoutS === null, () => {
      test(`${SERVICE}.linkAliveTimeoutS`, t('validations.invalid'), () => {
        enforce(
          Number.isInteger(service.linkAliveTimeoutS) &&
            Number(service.linkAliveTimeoutS) > 0
        ).isTruthy();
      });
    });

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });

    for (const field of [
      'defaultArrivalTime',
      'defaultDepartureTime',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });

      omitWhen(!service[field], () => {
        test(`${SERVICE}.${field}`, t('validations.invalid'), () => {
          enforce(service[field]).matches(TIME_OF_DAY);
        });
      });
    }
  },
};
